import { useCallback, useMemo } from 'react';

import { useMemberNodeId } from '@trello/business-logic-react/member';

import type { Account } from './Planner.types';
import { usePlannerMemberAccountFragment } from './PlannerMemberAccountFragment.generated';

export const usePlannerAccounts = () => {
  const memberNodeId = useMemberNodeId();

  const { data } = usePlannerMemberAccountFragment({
    from: { id: memberNodeId },
    optimistic: true,
  });

  const {
    accountsWithScopeIssues,
    allAccounts,
    expiredAccounts,
    problematicAccounts,
    validAccounts,
  } = useMemo(() => {
    const all: Account[] = [];
    const valid: Account[] = [];
    const problematic: Account[] = [];
    const expired: Account[] = [];
    const withScopeIssues: Account[] = [];

    const accounts = data?.planner?.accounts?.edges;
    if (accounts) {
      for (const account of accounts) {
        const accountNode = account.node;
        if (!accountNode) {
          continue;
        }

        all.push(accountNode);

        if (accountNode.isExpired) {
          problematic.push(accountNode);
          expired.push(accountNode);
        } else if (!accountNode.hasRequiredScopes) {
          problematic.push(accountNode);
          withScopeIssues.push(accountNode);
        } else {
          valid.push(accountNode);
        }
      }
    }

    return {
      accountsWithScopeIssues: withScopeIssues,
      allAccounts: all,
      expiredAccounts: expired,
      problematicAccounts: problematic,
      validAccounts: valid,
    };
  }, [data?.planner?.accounts?.edges]);

  /**
   * Returns the primary account ID for the user's planner accounts.
   *
   * Uses the primaryAccountId field from the backend if available,
   * otherwise finds the first valid account (has required scopes and not expired).
   *
   * @returns The ID of the primary planner account, or undefined if no valid account exists
   */
  const primaryAccountId = useMemo(
    () =>
      data?.planner?.primaryAccountId ||
      data?.planner?.accounts?.edges?.find(
        (edge) => edge?.node?.hasRequiredScopes && !edge?.node?.isExpired,
      )?.node?.id ||
      undefined,
    [data?.planner?.accounts?.edges, data?.planner?.primaryAccountId],
  );

  const primaryAccount = useMemo(
    () =>
      data?.planner?.accounts?.edges?.find(
        (edge) => edge?.node?.id === primaryAccountId,
      ),
    [data?.planner?.accounts?.edges, primaryAccountId],
  );

  /**
   * Returns the enabled calendars for a specific account ID.
   *
   * @param accountId - The account ID to get enabled calendars for
   * @returns Array of enabled calendars for the account
   */
  const getEnabledCalendarsForAccount = useCallback(
    (accountId: string) => {
      // Find the account in validAccounts and return its enabled calendars
      const account = validAccounts.find((acc) => acc?.id === accountId);
      const calendars = account?.enabledCalendars?.edges
        ?.map((edge) => edge?.node)
        .filter(Boolean);

      return calendars && calendars.length > 0 ? calendars : undefined;
    },
    [validAccounts],
  );

  const hasOneAccount = useMemo(() => {
    return allAccounts.length === 1;
  }, [allAccounts]);

  const hasReachedAccountLimit = useMemo(() => {
    return allAccounts.length >= 10;
  }, [allAccounts]);

  /**
   * Sorts a list of accounts so the primary account is first, then
   * alphabetically by display name. Also ensures, for type safety, that null
   * accounts are filtered out.
   *
   * @param accounts - The accounts to sort and sanitize
   * @returns The sorted and sanitized accounts
   */
  const sortAndSanitizeAccounts = useCallback(
    (accounts: Account[]) => {
      return accounts
        .filter((account) => !!account)
        .sort((a, b) => {
          if (a?.id === primaryAccountId) {
            return -1;
          } else if (b?.id === primaryAccountId) {
            return 1;
          }
          return a?.displayName?.localeCompare(b?.displayName ?? '') ?? 1;
        });
    },
    [primaryAccountId],
  );

  return {
    accountsWithScopeIssues,
    allAccounts,
    expiredAccounts,
    getEnabledCalendarsForAccount,
    hasOneAccount,
    hasReachedAccountLimit,
    primaryAccount,
    primaryAccountId,
    problematicAccounts,
    validAccounts,
    sortAndSanitizeAccounts,
  };
};
